import { useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { CardSkeletons } from '@todo/web-ui/components/card-skeletons';
import { CollectionGuide, TopicLinks, useCollectionCopy } from '@todo/web-ui/components/collection-guide';
import { EmptyState } from '@todo/web-ui/components/empty-state';
import { NotFoundScreen } from '@todo/web-ui/components/not-found-screen';
import { Intro, Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { CardGrid, Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { TourGrid } from '@todo/web-ui/components/tour-grid';
import {
  COLLECTION_DUPLICATES_SEED_KEY,
  collectionBySlug,
  collectionSeedKey,
  isCollectionIndexable,
  topicsOf,
  type Collection,
} from '@todo/web-ui/lib/collections';
import { COLLECTIONS } from '@/lib/collections';
import { collectionQuery } from '@/lib/prefetch';
import { useTours } from '@todo/core/hooks/use-tours';
import { DEFAULT_LOCALE, isSupportedLocale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { LIST_BUCKET } from '@todo/core/list-queries';
import { breadcrumbJsonLd, faqPageJsonLd, seoTitle } from '@todo/core/seo';
import { getPrefetched } from '@todo/core/static-prefetch';

// One page per collection (src/lib/collections.ts): /everglades, /date-night,
// /south-beach… — dubai's screen on the same tours_by_theme RPC, minus what
// the site doesn't have yet: no "Read also" guides (no Sanity project).
// Indexable by dubai's rule (isCollectionIndexable): a guide of its own
// (`collections.<slug>.guide`), at least one tour, and a list that isn't a
// near copy of an earlier page — so a collection joins the sitemap with its
// guide.
export function generateStaticParams({ params }: { params: { lang?: string } }): { lang: string; collection: string }[] {
  const lang = isSupportedLocale(params.lang) ? params.lang : DEFAULT_LOCALE;
  return COLLECTIONS.map((c) => ({ lang, collection: c.slug }));
}

export default function CollectionRoute() {
  const { collection: slug } = useLocalSearchParams<{ collection: string }>();
  const collection = collectionBySlug(slug);
  // A slug that isn't a collection: only reachable by client-side navigation
  // (only collections are exported).
  if (!collection) return <NotFoundScreen />;
  return <CollectionScreen collection={collection} />;
}

function CollectionScreen({ collection }: { collection: Collection }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const seedKey = collectionSeedKey(collection.slug);
  const { tours, loading } = useTours(collectionQuery(collection.theme), seedKey);
  const title = t(`collections.${collection.slug}.title`);
  const intro = t(`collections.${collection.slug}.intro`);
  const copy = useCollectionCopy(collection.slug);
  // An SEO <title>, keyword <h1> and meta of its own where the copy has them;
  // otherwise the short title and the intro.
  const pageTitle = t(`collections.${collection.slug}.seoTitle`, { defaultValue: title });
  const heading = t(`collections.${collection.slug}.heading`, { defaultValue: title });
  const meta = t(`collections.${collection.slug}.meta`, { defaultValue: intro });
  // A topic sits under its category in the breadcrumb; a category links to its topics.
  const parentCrumb = collection.parent
    ? [{ name: t(`collections.${collection.parent}.title`), path: collection.parent }]
    : [];
  const parent = collectionBySlug(collection.parent);
  const related = parent ? [parent] : topicsOf(collection.slug);
  // The build's verdict, baked into the page so the client's head matches.
  const duplicateOf = getPrefetched<Record<string, string>>(LIST_BUCKET, COLLECTION_DUPLICATES_SEED_KEY)?.[collection.slug];

  return (
    <Page>
      <PageHead
        title={seoTitle(pageTitle)}
        description={meta}
        path={collection.slug}
        indexable={isCollectionIndexable(copy.guide.length > 0, tours.length, duplicateOf)}>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [...parentCrumb, { name: title }]))}
        </script>
        {copy.faq.length ? <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script> : null}
        {seedScript(seedKey)}
        {seedScript(COLLECTION_DUPLICATES_SEED_KEY)}
      </PageHead>

      <Section id="collection-title" level={1} title={heading}>
        <Intro>{intro}</Intro>
        {related.length ? <TopicLinks collections={related} /> : null}
        {tours.length ? (
          <TourGrid tours={tours} />
        ) : loading ? (
          <CardGrid>
            <CardSkeletons />
          </CardGrid>
        ) : (
          <EmptyState exclude={collection.slug} />
        )}
      </Section>

      <CollectionGuide slug={collection.slug} copy={copy} />
    </Page>
  );
}
